import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { ensureSyntaxTree, syntaxTree } from '@codemirror/language';
import { linter, Diagnostic } from '@codemirror/lint';
import { Extension, EditorState, StateField, RangeSetBuilder } from '@codemirror/state';
import { EditorView, DecorationSet, Decoration } from '@codemirror/view';
import { HTMLHint } from 'htmlhint';
import { JSHINT } from 'jshint';

import { Language, ReadOnlyRange } from './store/types';

const CONSENT_ATTRIBUTE = 'data-consent-type';
const CONSENT_TYPES = ['analytics', 'marketing'] as const;

/** For StudioCodeEditor's `extraHtmlAttributes`: lang-html does not know this Goldcast attribute. */
export const CONSENT_ATTRIBUTE_COMPLETION: Record<string, readonly string[]> = {
  [CONSENT_ATTRIBUTE]: CONSENT_TYPES
};

export function baseThemeExtension(maxHeight): Extension {
  const baseTheme = EditorView.baseTheme({
    '&': {
      maxHeight: maxHeight,
      width: '100%',
      height: 'auto',
      border: '1px solid #d1d5db'
    },
    '.cm-scroller *': {
      fontFamily: "Menlo, Monaco, 'Courier New', monospace",
      fontSize: '14px'
    }
  });
  return baseTheme;
}

export function languageExtension(
  language: Language,
  extraHtmlAttributes?: Record<string, readonly string[]>
): Extension {
  switch (language) {
    case 'javascript':
      return [
        javascript(),
        jsLinter({
          esversion: 11,
          latedef: true,
          undef: true,
          browser: true,
          devel: true,
          asi: true,
          sub: true,
          globals: {
            //Globals for common tracking pixel window variables
            Bizible: true,
            dataLayer: true,
            fbq: true,
            twq: true
          }
        })
      ];
    case 'html':
      return [
        html(extraHtmlAttributes ? { extraGlobalAttributes: extraHtmlAttributes } : {}),
        htmlLinter({
          'tag-pair': true,
          'spec-char-escape': true,
          'id-unique': true,
          'src-not-empty': true,
          'attr-no-duplication': true
        })
      ];
    default:
      return [];
  }
}

export function restrictedKeywordsCheck(restrictedKeywords: String[]): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    syntaxTree(view.state)
      .cursor()
      .iterate(node => {
        const text = view.state.doc.sliceString(node.from, node.to);
        if (restrictedKeywords.includes(text.toLowerCase())) {
          diagnostics.push({
            from: node.from,
            to: node.to,
            message: `Use of this keyword is not allowed: ${text}`,
            severity: 'error',
            actions: [
              {
                name: 'Remove',
                apply(view, from, to) {
                  view.dispatch({ changes: { from, to } });
                }
              }
            ]
          });
        }
      });
    return diagnostics;
  });
}

export function restrictedTagsCheck(restrictedTags: String[]): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    const re = new RegExp(`<[/\\s]*(${restrictedTags.join('|')})(?=\\s|>)[^><]*>`, 'gi');
    let match;
    while ((match = re.exec(view.state.doc.toString())) !== null) {
      const { index } = match;
      diagnostics.push({
        from: index,
        to: index + match[0].length,
        message: `Use of this tag is not allowed: ${match[0]}`,
        severity: 'error',
        actions: [
          {
            name: 'Remove',
            apply(view, from, to) {
              view.dispatch({ changes: { from, to } });
            }
          }
        ]
      });
    }
    return diagnostics;
  });
}

function jsLinter(jsHintOptions): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    JSHINT(view.state.doc.toString(), jsHintOptions);
    const errors = JSHINT.errors;
    if (errors.length > 0) {
      for (const error of errors) {
        let errorLength = 0;
        const { line, character, reason, a, evidence } = error;
        if (evidence && a && evidence.includes(a)) {
          errorLength = a.length;
        }
        diagnostics.push({
          from: view.state.doc.line(line).from + character - 1,
          to: view.state.doc.line(line).from + character - 1 + errorLength,
          message: reason,
          severity: 'error'
        });
      }
    }
    return diagnostics;
  });
}

export function htmlLinter(htmlHintRuleset): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    const htmlHint = HTMLHint.verify(view.state.doc.toString() || '', htmlHintRuleset);
    if (htmlHint.length > 0) {
      for (const error of htmlHint) {
        const { line, col, message } = error;
        diagnostics.push({
          from: view.state.doc.line(line).from + col - 1,
          to: view.state.doc.line(line).from + col - 1,
          message,
          severity: 'error'
        });
      }
    }
    return diagnostics;
  });
}

export function readOnlyExtension(readOnlyRanges: ReadOnlyRange[]): Extension[] {
  const readOnlyField = defineReadOnlyField(readOnlyRanges);
  return [readOnlyField, readOnlyCheck(readOnlyField), readOnlyTheme()];
}

function readOnlyTheme(): Extension {
  return EditorView.theme({
    '.cm-readOnly, .cm-readOnly.cm-line span': { color: 'gray' }
  });
}

function defineReadOnlyField(ranges: ReadOnlyRange[]): StateField<DecorationSet> {
  const readOnlyDecoration = Decoration.line({
    class: 'cm-readOnly'
  });

  return StateField.define<DecorationSet>({
    create(state) {
      const builder = new RangeSetBuilder<Decoration>();
      for (let { fromLine, toLine } of ranges) {
        if (fromLine === 'last') {
          fromLine = state.doc.lines;
        }
        if (toLine === 'last') {
          toLine = state.doc.lines;
        }

        for (let line = fromLine; line <= toLine; line += 1) {
          const pos = state.doc.line(line).from;
          builder.add(pos, pos, readOnlyDecoration);
        }
      }
      return builder.finish();
    },
    update(readOnlyLines, tr) {
      readOnlyLines = readOnlyLines.map(tr.changes);
      return readOnlyLines;
    },
    provide: f => EditorView.decorations.from(f)
  });
}

function readOnlyCheck(readOnlyField: StateField<DecorationSet>): Extension {
  return EditorState.transactionFilter.of(tr => {
    const readOnlyRangeSet = tr.startState.field(readOnlyField, false);
    if (readOnlyRangeSet && tr.docChanged) {
      let block = false;
      tr.changes.iterChangedRanges((chFrom, chTo) => {
        const roIter = readOnlyRangeSet.iter();
        while (roIter.value) {
          const roFrom = tr.startState.doc.lineAt(roIter.from).from;
          const roTo = tr.startState.doc.lineAt(roIter.from).to;
          if (chTo >= roFrom && chFrom <= roTo) {
            block = true;
            return false;
          }
          roIter.next();
        }
      });
      if (block) {
        return [];
      }
    }
    return tr;
  });
}

const TRACKING_TAGS = new Set(['script', 'img', 'iframe', 'embed', 'object', 'video', 'audio', 'source']);
const VALID_CONSENT_TYPES = new Set<string>(CONSENT_TYPES);
const EXEMPT_SCRIPT_TYPES = new Set(['application/ld+json', 'application/json']);

export interface ConsentTag {
  /** Lowercased tag name, e.g. `script`. */
  tagName: string;
  /** The declared `data-consent-type`, or null when the attribute is absent. */
  category: string | null;
  /** Range of the opening (or self-closing) tag. */
  from: number;
  to: number;
  /** Range of an existing `data-consent-type` attribute, for replacement. */
  attributeRange: { from: number; to: number } | null;
  /** Where a new attribute goes: just before the closing `>` or `/>`. */
  insertAt: number;
}

function stripQuotes(value: string): string {
  const quote = value[0];
  return (quote === '"' || quote === "'") && value.endsWith(quote) ? value.slice(1, -1) : value;
}

/**
 * Every tracking-capable tag in the document, with its declared consent category.
 *
 * Walks the HTML syntax tree rather than scanning the text: `<!-- commented out -->`
 * markup parses as a Comment node and markup inside a `<script>` string literal parses
 * as JavaScript, so neither is mistaken for a tag the organizer has to classify. The
 * backend's BeautifulSoup pass (`find_invalid_consent_tags`) exempts both the same way,
 * and the editor must not reject a blob the API would accept.
 */
export function scanConsentTags(state: EditorState): ConsentTag[] {
  const tags: ConsentTag[] = [];
  // syntaxTree() returns only what has been parsed so far, which right after the editor is
  // created is nothing. A partial tree would silently under-report — an unclassified tag
  // would neither appear in the preview nor block an enforced save. These fields cap at
  // 4096 characters, so parsing the whole document is immediate.
  const tree = ensureSyntaxTree(state, state.doc.length, 1000) ?? syntaxTree(state);

  tree.cursor().iterate(nodeRef => {
    if (nodeRef.name !== 'OpenTag' && nodeRef.name !== 'SelfClosingTag') {
      return;
    }
    const tagNode = nodeRef.node;
    const tagNameNode = tagNode.getChild('TagName');
    if (!tagNameNode) {
      return;
    }
    const tagName = state.sliceDoc(tagNameNode.from, tagNameNode.to).toLowerCase();
    if (!TRACKING_TAGS.has(tagName)) {
      return;
    }

    let scriptType = '';
    let category: string | null = null;
    let attributeRange: { from: number; to: number } | null = null;

    for (const attribute of tagNode.getChildren('Attribute')) {
      const nameNode = attribute.getChild('AttributeName');
      if (!nameNode) {
        continue;
      }
      const valueNode = attribute.getChild('AttributeValue') ?? attribute.getChild('UnquotedAttributeValue');
      // Attribute names are case-insensitive in HTML; values are not — the consent
      // category must match `analytics`/`marketing` exactly, lowercase.
      const name = state.sliceDoc(nameNode.from, nameNode.to).toLowerCase();
      const value = valueNode ? stripQuotes(state.sliceDoc(valueNode.from, valueNode.to)) : '';

      if (name === 'type') {
        scriptType = value;
      } else if (name === CONSENT_ATTRIBUTE) {
        category = value;
        attributeRange = { from: attribute.from, to: attribute.to };
      }
    }

    if (tagName === 'script' && EXEMPT_SCRIPT_TYPES.has(scriptType)) {
      return;
    }

    const endTagNode = tagNode.getChild('EndTag');
    tags.push({
      tagName,
      category,
      from: tagNode.from,
      to: tagNode.to,
      attributeRange,
      insertAt: endTagNode ? endTagNode.from : tagNode.to - 1
    });
  });

  return tags;
}

function applyCategory(category: string) {
  // CodeMirror maps `from`/`to` through any edits made since the diagnostic fired, so the
  // tag is located in the *current* document rather than at positions captured earlier.
  // The mapped `to` is the sharper anchor: an insertion at the very start of the document
  // leaves `from` pointing before the inserted text, so it no longer matches the tag.
  return (view: EditorView, from: number, to: number) => {
    const tags = scanConsentTags(view.state);
    const tag =
      tags.find(candidate => candidate.to === to) ?? tags.find(candidate => candidate.from < to && candidate.to > from);
    if (!tag) {
      return;
    }
    view.dispatch({
      changes: tag.attributeRange
        ? { ...tag.attributeRange, insert: `${CONSENT_ATTRIBUTE}="${category}"` }
        : { from: tag.insertAt, to: tag.insertAt, insert: ` ${CONSENT_ATTRIBUTE}="${category}"` }
    });
  };
}

export function consentTypeDiagnostics(state: EditorState, severity: 'error' | 'warning'): Diagnostic[] {
  return scanConsentTags(state)
    .filter(tag => !VALID_CONSENT_TYPES.has(tag.category ?? ''))
    .map(tag => ({
      from: tag.from,
      to: tag.to,
      severity,
      message: 'Tracking-capable tags require data-consent-type="analytics" or "marketing".',
      actions: [
        { name: 'Mark as analytics', apply: applyCategory('analytics') },
        { name: 'Mark as marketing', apply: applyCategory('marketing') }
      ]
    }));
}

export function consentTypeAttributeCheck(severity: 'error' | 'warning'): Extension {
  return linter(view => consentTypeDiagnostics(view.state, severity));
}

export function characterLimitExtension(characterLimit: number): Extension {
  return EditorState.changeFilter.of(tr => {
    if (!tr.docChanged || characterLimit <= 0) {
      return true;
    }
    const docLength = tr.newDoc.toString().length;
    return docLength <= characterLimit;
  });
}
